/**
 * Image optimization utilities for better performance
 */

import { logger } from '../utils/logger';

// Image size configurations
export const IMAGE_SIZES = {
  thumbnail: { width: 150, height: 150 },
  small: { width: 300, height: 300 },
  medium: { width: 600, height: 600 },
  large: { width: 1200, height: 1200 },
} as const;

export type ImageSize = keyof typeof IMAGE_SIZES;

// Check if browser supports WebP
export const supportsWebP = (): boolean => {
  if (typeof window === 'undefined') return false;

  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
};

// Check if browser supports AVIF
export const supportsAVIF = (): boolean => {
  if (typeof window === 'undefined') return false;

  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
};

// Generate optimized image URL with format and size
export const getOptimizedImageUrl = (
  originalUrl: string,
  size: ImageSize = 'medium',
  quality: number = 80,
): string => {
  if (!originalUrl) return '';

  // If it's already an optimized URL or external URL, return as is
  if (originalUrl.includes('?') || originalUrl.startsWith('http')) {
    return originalUrl;
  }

  const { width, height } = IMAGE_SIZES[size];
  const hasWebP = supportsWebP();
  const hasAVIF = supportsAVIF();

  // Determine best format
  let format = 'jpeg';
  if (hasAVIF) {
    format = 'avif';
  } else if (hasWebP) {
    format = 'webp';
  }

  // For local images, we can't actually convert them, but we can add parameters for future CDN integration
  return `${originalUrl}?w=${width}&h=${height}&q=${quality}&f=${format}`;
};

// Generate srcset for responsive images
export const generateSrcSet = (
  originalUrl: string,
  sizes: ImageSize[] = ['small', 'medium', 'large'],
): string => {
  if (!originalUrl) return '';

  return sizes
    .map((size) => {
      const { width } = IMAGE_SIZES[size];
      const optimizedUrl = getOptimizedImageUrl(originalUrl, size);
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
};

// Generate sizes attribute for responsive images
export const generateSizesAttribute = (): string => {
  return '(max-width: 640px) 300px, (max-width: 1024px) 600px, 1200px';
};

// Preload critical images
export const preloadImage = (url: string, size: ImageSize = 'medium'): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const optimizedUrl = getOptimizedImageUrl(url, size);

    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to preload image: ${optimizedUrl}`));

    img.src = optimizedUrl;
  });
};

// Lazy load image with intersection observer
export const createLazyImageObserver = (
  callback: (entry: IntersectionObserverEntry) => void,
): IntersectionObserver => {
  const options = {
    root: null,
    rootMargin: '50px', // Start loading 50px before image comes into view
    threshold: 0.1,
  };

  return new IntersectionObserver((entries) => {
    entries.forEach(callback);
  }, options);
};

// Image compression utility (for user uploads)
export const compressImage = (
  file: File,
  maxWidth: number = 1200,
  maxHeight: number = 1200,
  quality: number = 0.8,
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        'image/jpeg',
        quality,
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));

    try {
      img.src = URL.createObjectURL(file);
    } catch {
      reject(new Error('Failed to create object URL'));
    }
  });
};

// Cache management for images with expiration
interface CacheEntry {
  value: string;
  timestamp: number;
}

class ImageCache {
  private cache = new Map<string, CacheEntry>();
  private maxSize = 50; // Maximum number of cached images
  private expirationTime = 40 * 60 * 1000; // 40 minutes in milliseconds
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Start periodic cleanup every 40 minutes
    this.startPeriodicCleanup();
  }

  set(key: string, value: string): void {
    // Clean up expired entries first
    this.cleanupExpired();

    if (this.cache.size >= this.maxSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, {
      value,
      timestamp: Date.now(),
    });
  }

  get(key: string): string | undefined {
    const entry = this.cache.get(key);
    if (!entry) {
      return undefined;
    }

    // Check if entry is expired
    if (Date.now() - entry.timestamp > this.expirationTime) {
      this.cache.delete(key);
      return undefined;
    }

    return entry.value;
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) {
      return false;
    }

    // Check if entry is expired
    if (Date.now() - entry.timestamp > this.expirationTime) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  clear(): void {
    this.cache.clear();
  }

  // Clean up expired entries
  private cleanupExpired(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.expirationTime) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach((key) => {
      this.cache.delete(key);
      logger.log('Deleted expired image cache entry:', key);
    });
  }

  // Start periodic cleanup every 40 minutes
  private startPeriodicCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    this.cleanupInterval = setInterval(() => {
      this.cleanupExpired();
    }, this.expirationTime);
  }

  // Get cache statistics
  getStats(): { size: number; maxSize: number; expirationTime: number } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      expirationTime: this.expirationTime,
    };
  }

  // Cleanup on destroy
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.clear();
  }
}

export const imageCache = new ImageCache();
